import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

import {
  FaArrowLeft,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaClock,
  FaBan,
  FaTicketAlt,
  FaCalendarAlt,
  FaFutbol,
  FaCoins,
  FaChartLine,
  FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tickets/${id}`,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );

      setTicket(response.data.ticket || response.data);
    } catch (err) {
      console.error("Failed to fetch ticket:", err);

      setError(
        err.response?.data?.error ||
          "Failed to load ticket. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const formatMoney = (value) => {
    if (value === null || value === undefined) return "-";

    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMatchDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "won":
        return {
          label: "Won",
          color: "bg-green-900/60 text-green-300 border-green-500/30",
          icon: <FaCheck />,
        };

      case "lost":
        return {
          label: "Lost",
          color: "bg-red-900/60 text-red-300 border-red-500/30",
          icon: <FaTimes />,
        };

      case "cancelled":
        return {
          label: "Cancelled",
          color: "bg-gray-700/60 text-gray-300 border-gray-500/30",
          icon: <FaBan />,
        };

      default:
        return {
          label: "Pending",
          color: "bg-blue-900/60 text-blue-300 border-blue-500/30",
          icon: <FaClock />,
        };
    }
  };

  const getSelectionStatus = (status) => {
    switch (status) {
      case "won":
        return {
          className: "bg-green-900/50 text-green-300",
          icon: <FaCheck />,
        };

      case "lost":
        return {
          className: "bg-red-900/50 text-red-300",
          icon: <FaTimes />,
        };

      case "cancelled":
        return {
          className: "bg-gray-700 text-gray-300",
          icon: <FaBan />,
        };

      default:
        return {
          className: "bg-blue-900/50 text-blue-300",
          icon: <FaClock />,
        };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />
            <p className="text-gray-300">Loading ticket...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !ticket) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-4xl mx-auto p-4 md:p-6">

            <button
              onClick={() => navigate("/tickets")}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
            >
              <FaArrowLeft />
              Back to Tickets
            </button>

            <div className="bg-gray-800/60 border border-red-500/20 rounded-xl p-8 text-center">
              <FaExclamationTriangle className="text-red-400 text-4xl mx-auto mb-4" />

              <h2 className="text-xl font-semibold text-white">
                Unable to load ticket
              </h2>

              <p className="text-gray-400 mt-2">
                {error || "Ticket not found."}
              </p>

              <button
                onClick={fetchTicket}
                className="mt-6 px-5 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const status = getStatusConfig(ticket.status);

  const selections =
    ticket.selections ||
    ticket.ticket_selections ||
    [];

  const project = ticket.project;

  const wonSelections = selections.filter(
    (selection) => selection.status === "won",
  ).length;

  const lostSelections = selections.filter(
    (selection) => selection.status === "lost",
  ).length;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-6xl mx-auto p-4 md:p-6">

          {/* Back */}
          <button
            onClick={() => navigate("/tickets")}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-5 transition-colors"
          >
            <FaArrowLeft />
            Back to Tickets
          </button>

          {/* Ticket header */}
          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden mb-6">

            <div className="p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-600/30 to-emerald-600/20 border border-green-500/20 flex items-center justify-center">
                    <FaTicketAlt className="text-green-400 text-2xl" />
                  </div>

                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-2xl md:text-3xl font-bold text-white">
                        Ticket #{ticket.ticket_number || ticket.id}
                      </h1>

                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm border ${status.color}`}
                      >
                        {status.icon}
                        {status.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-400">

                      {project && (
                        <button
                          onClick={() =>
                            navigate(`/projects/${project.id}`)
                          }
                          className="text-green-400 hover:text-green-300 transition-colors"
                        >
                          {project.name}
                        </button>
                      )}

                      <span className="flex items-center gap-1">
                        <FaCalendarAlt />
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {ticket.owner_username && (
                  <div className="bg-gray-700/40 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500">
                      Ticket Owner
                    </p>
                    <p className="text-sm text-white font-medium mt-1">
                      {ticket.owner_username}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Financial stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

            <div className="bg-gray-800/60 border border-green-500/20 rounded-xl p-4">
              <p className="text-sm text-gray-400">
                Stake
              </p>

              <div className="flex items-center gap-2 mt-2">
                <FaCoins className="text-green-400" />

                <span className="text-xl font-bold text-white">
                  {formatMoney(ticket.stake)}
                </span>
              </div>
            </div>

            <div className="bg-gray-800/60 border border-blue-500/20 rounded-xl p-4">
              <p className="text-sm text-gray-400">
                Total Odd
              </p>

              <div className="flex items-center gap-2 mt-2">
                <FaChartLine className="text-blue-400" />

                <span className="text-xl font-bold text-white">
                  {ticket.total_odd}
                </span>
              </div>
            </div>

            <div className="bg-gray-800/60 border border-purple-500/20 rounded-xl p-4">
              <p className="text-sm text-gray-400">
                Potential Return
              </p>

              <div className="flex items-center gap-2 mt-2">
                <FaCoins className="text-purple-400" />

                <span className="text-xl font-bold text-white">
                  {formatMoney(ticket.potential_return)}
                </span>
              </div>
            </div>

            <div className="bg-gray-800/60 border border-gray-500/20 rounded-xl p-4">
              <p className="text-sm text-gray-400">
                Selections
              </p>

              <div className="flex items-center gap-2 mt-2">
                <FaFutbol className="text-gray-300" />

                <span className="text-xl font-bold text-white">
                  {selections.length}
                </span>
              </div>
            </div>
          </div>

          {/* Selection result summary */}
          {selections.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-6">

              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">
                  Total
                </p>
                <p className="text-lg font-bold text-white">
                  {selections.length}
                </p>
              </div>

              <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-3 text-center">
                <p className="text-xs text-green-400">
                  Won
                </p>
                <p className="text-lg font-bold text-green-400">
                  {wonSelections}
                </p>
              </div>

              <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-3 text-center">
                <p className="text-xs text-red-400">
                  Lost
                </p>
                <p className="text-lg font-bold text-red-400">
                  {lostSelections}
                </p>
              </div>

            </div>
          )}

          {/* Selections */}
          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden">

            <div className="px-5 py-4 border-b border-green-500/20 flex items-center justify-between">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <FaFutbol className="text-green-400" />
                Selections
              </h2>

              <span className="text-xs bg-gray-700 px-2.5 py-1 rounded-full text-gray-300">
                {selections.length}
              </span>
            </div>

            {selections.length > 0 ? (
              <div className="divide-y divide-green-500/10">

                {selections.map((selection) => {
                  const selectionStatus = getSelectionStatus(
                    selection.status,
                  );

                  const matchId = selection.match_id;

                  return (
                    <div
                      key={selection.id}
                      className="p-5 hover:bg-gray-700/20 transition-colors"
                    >
                      {/* Match */}
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">

                        <div className="flex-1 min-w-0">

                          {/* League */}
                          {selection.league && (
                            <div className="text-xs text-gray-500 mb-3">
                              {selection.league}
                            </div>
                          )}

                          {/* Teams */}
                          <div className="flex items-center gap-4">

                            <div className="flex-1">

                              <div className="flex items-center gap-3 mb-2">
                                {selection.homeLogo ? (
                                  <img
                                    src={selection.homeLogo}
                                    alt=""
                                    className="w-7 h-7 object-contain"
                                  />
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center">
                                    <FaFutbol className="text-gray-500 text-xs" />
                                  </div>
                                )}

                                <span className="text-white font-medium">
                                  {selection.homeTeam ||
                                    "Home Team"}
                                </span>

                                {selection.goals_home !==
                                  null &&
                                  selection.goals_home !==
                                    undefined && (
                                    <span className="ml-auto font-bold text-white">
                                      {selection.goals_home}
                                    </span>
                                  )}
                              </div>

                              <div className="flex items-center gap-3">
                                {selection.awayLogo ? (
                                  <img
                                    src={selection.awayLogo}
                                    alt=""
                                    className="w-7 h-7 object-contain"
                                  />
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center">
                                    <FaFutbol className="text-gray-500 text-xs" />
                                  </div>
                                )}

                                <span className="text-white font-medium">
                                  {selection.awayTeam ||
                                    "Away Team"}
                                </span>

                                {selection.goals_away !==
                                  null &&
                                  selection.goals_away !==
                                    undefined && (
                                    <span className="ml-auto font-bold text-white">
                                      {selection.goals_away}
                                    </span>
                                  )}
                              </div>
                            </div>

                            <div className="hidden md:block text-gray-600">
                              <FaChevronRight />
                            </div>

                          </div>

                          {/* Match date */}
                          {selection.date && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-3">
                              <FaCalendarAlt />
                              {formatMatchDate(selection.date)}
                            </div>
                          )}
                        </div>

                        {/* Bet */}
                        <div className="lg:w-64 bg-gray-700/30 rounded-xl p-4 border border-gray-700/50">

                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-gray-500">
                              Market
                            </span>

                            <span className="text-xs text-gray-300">
                              {selection.market}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-gray-500">
                              Selection
                            </span>

                            <span className="text-sm text-white font-medium">
                              {selection.selection}
                            </span>
                          </div>

                          <div className="flex items-center justify-between">

                            <div>
                              <span className="text-xs text-gray-500 block">
                                Odd
                              </span>

                              <span className="text-lg font-bold text-green-400">
                                {selection.odd}
                              </span>
                            </div>

                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${selectionStatus.className}`}
                            >
                              {selectionStatus.icon}
                              {selection.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-10 text-center">
                <FaFutbol className="text-gray-600 text-3xl mx-auto mb-3" />

                <p className="text-gray-400">
                  No selections found for this ticket.
                </p>
              </div>
            )}
          </div>

          {/* Footer information */}
          <div className="mt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-gray-500">
            <span>
              Created: {formatDate(ticket.created_at)}
            </span>

            {ticket.settled_at && (
              <span>
                Settled: {formatDate(ticket.settled_at)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TicketDetails;